/**
 * A valasztott eszkozok megjegyzese.
 *
 * Aki fejhallgatoval dolgozik, az MINDEN hivasban fejhallgatoval akar
 * dolgozni - nem azert valasztotta ki, mert aznap ugy hozta a kedve. Enelkul
 * minden oleles a bongeszo alapertelmezesevel indult, es ujra kellett
 * valasztani.
 *
 * ─── A CSAPDA ───────────────────────────────────────────────────────────────
 *
 * Az eszkozok JONNEK-MENNEK. A tegnap valasztott fejhallgato ma nincs bedugva,
 * es az elmentett azonositohoz nem tartozik semmi. Ilyenkor NEM eroltetjuk:
 * csendben az alapertelmezesre esunk vissza.
 *
 * Ez nem elmeleti ovatossag. A `switchActiveDevice` egy nem letezo
 * azonositora hibat dob, es abbol a felhasznalo egy hibauzenetet latna a hivas
 * INDULASAKOR - azert, mert kihuzta a fuleset. A visszaeses ezert a modul
 * feladata, nem a hivo fele.
 */

export type DeviceKind = 'audioinput' | 'audiooutput' | 'videoinput';

/** Egy eszkoz, ahogy a bongeszo adja. */
export interface Device {
  deviceId: string;
  label: string;
}

/**
 * Amit elteszunk: az azonosito ES a nev.
 *
 * A nev nem diszites, hanem a masodik esely - lasd `resolveDevice`.
 */
export interface SavedDevice {
  id: string;
  label: string;
}

const STORAGE_KEY = 'avslack.huddle.devices';

/**
 * A megjegyzett eszkoz feloldasa az ELO listara.
 *
 * Ket lepcso, szandekosan ebben a sorrendben:
 *
 *  1. **Azonosito szerint.** Ez a pontos egyezes; amig az engedely tartos, a
 *     bongeszo ugyanazt az azonositot adja vissza.
 *  2. **Nev szerint, de csak ha EGYETLEN ilyen van.** A bongeszo ujrasorsolja
 *     az azonositokat, ha az oldal engedelye megszunt es ujra megkaptuk -
 *     ilyenkor ugyanaz a fizikai fejhallgato mas azonositot kap. A nev viszont
 *     megmarad.
 *
 * A "csak ha egyetlen ilyen van" kikotes fontos: ket azonos nevu eszkoznel
 * (ket ugyanolyan tipusu webkamera) a nev alapjan valasztani talalgatas lenne,
 * es a rossz eszkozre kapcsolni rosszabb, mint az alapertelmezesen hagyni.
 *
 * @returns a hasznalando azonosito, vagy `null` = maradjon az alapertelmezes.
 */
export function resolveDevice(saved: SavedDevice | null, available: Device[]): string | null {
  if (!saved) return null;

  if (available.some((device) => device.deviceId === saved.id)) return saved.id;

  if (!saved.label) return null;
  const byLabel = available.filter((device) => device.label === saved.label);
  return byLabel.length === 1 ? (byLabel[0]?.deviceId ?? null) : null;
}

/**
 * A bongeszo eszkozlistajanak TISZTITASA a valasztohoz.
 *
 * A Chrome (Windows/ChromeOS) a valodi eszkozok MELLE ket VIRTUALIS bejegyzest is ad:
 * `default` ("Default - Fejhallgato") es `communications` ("Communications - ..."). Ugyanaz
 * a fizikai eszkoz igy ketszer-haromszor szerepel a listan, es a felhasznalo nem tudja,
 * melyiket valassza - a kijelolt "pont" pedig a masik soron latszik, mint amit ok valasztottak.
 *
 *  - a `communications` elhagyjuk (a `default` ugyanazt a celt szolgalja);
 *  - a `default` megmarad, de ERTHETO neven: "Rendszer alapértelmezése (Fejhallgato)" - ez
 *    kovet, ha az operacios rendszer alapertelmezettje valtozik;
 *  - az ismetlodo azonositokat egyszer mutatjuk.
 */
export function tidyDevices(list: Device[]): Device[] {
  const seen = new Set<string>();
  const result: Device[] = [];

  for (const device of list) {
    if (device.deviceId === 'communications') continue;
    if (seen.has(device.deviceId)) continue;
    seen.add(device.deviceId);

    if (device.deviceId === 'default') {
      const name = device.label.replace(/^(default|alapértelmezett)\s*[-–—:]\s*/i, '').trim();
      result.push({
        deviceId: device.deviceId,
        label: name ? `Rendszer alapértelmezése (${name})` : 'Rendszer alapértelmezése',
      });
      continue;
    }
    result.push(device);
  }

  // A rendszer alapertelmezettje legyen az elso: ez az, amit a legtobben akarnak.
  const index = result.findIndex((device) => device.deviceId === 'default');
  if (index > 0) result.unshift(...result.splice(index, 1));
  return result;
}

/**
 * A tarolt valasztasok - fajtankent egy.
 *
 * SOSEM dob: privat ablakban vagy tiltott tarolasnal a hivas menjen tovabb,
 * csak eppen ne emlekezzen semmire.
 */
function readAll(): Partial<Record<DeviceKind, SavedDevice>> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object'
      ? (parsed as Partial<Record<DeviceKind, SavedDevice>>)
      : {};
  } catch {
    return {};
  }
}

export function rememberedDevice(kind: DeviceKind): SavedDevice | null {
  const saved = readAll()[kind];
  return saved && typeof saved.id === 'string' && typeof saved.label === 'string' ? saved : null;
}

export function rememberDevice(kind: DeviceKind, device: Device): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...readAll(), [kind]: { id: device.deviceId, label: device.label } }),
    );
  } catch {
    // A tarolas hianya nem torheti el a hivast.
  }
}
